// src/config/menuConfig.js
// 云平台（储能管家）信息架构 —— 前台 / 后台 两大分区
// 来源：《云平台（能源管家）需求文档》《云平台单站控制（削峰填谷/光储充）需求文档》《云平台账号权限》
import React from 'react';
import {
  DashboardOutlined, FundOutlined,
  MonitorOutlined,
  AlertOutlined,
  LineChartOutlined,
  ToolOutlined,
  SettingOutlined, ShopOutlined,
} from '@ant-design/icons';

const L = (zh, en) => ({ zh, en });

// 按当前语言取文案
export const pick = (value, lang) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const l = String(lang || 'zh').split('-')[0];
  return value[l] || value.zh || value.en || '';
};

export const IA = [
  {
    key: 'front',
    type: 'group',
    label: L('前台', 'Front Office'),
    children: [
      {
        key: 'sec-display', icon: DashboardOutlined, label: L('展示中心', 'Display Center'),
        children: [
          { key: '/app/display/screen', label: L('数据大屏', 'Data Screen'), desc: L('全屏可视化大屏，集中展示各站点运行态势与关键指标', 'Full-screen visualization of station operation and KPIs') },
          { key: '/app/display/multi', label: L('多站概览', 'Multi-Station'), desc: L('汇总所辖站点的容量、发电、收益、告警与运行状态', 'Aggregated capacity, generation, revenue, alarms and status') },
          { key: '/app/display/single', label: L('单站概览', 'Single Station'), desc: L('单站点能耗收益、电站信息、能量流转与运行曲线', 'Station energy, revenue, information, energy flow and curves') },
          { key: '/app/display/map', label: L('站点地图', 'Station Map'), desc: L('按地理分布查看站点位置与运行状态', 'View station locations and status on a map') },
          { key: '/app/display/3d', label: L('三维可视化', '3D Visualization'), desc: L('站点三维建模可视化展示（增值功能）', '3D modeled visualization of stations (value-added)') },
        ],
      },
      {
        key: 'sec-monitor', icon: MonitorOutlined, label: L('监控中心', 'Monitoring Center'),
        children: [
          { key: '/app/monitor/realtime', label: L('实时运行', 'Real-time'), desc: L('实时查看各站点功率、SOC 与设备运行状态', 'Real-time power, SOC and device status') },
          { key: '/app/monitor/station', label: L('单站监控', 'Station Monitor'), desc: L('单站设备监控、曲线报表、控制中心与系统管理', 'Device monitoring, curves, control and system management') },
          { key: '/app/monitor/video', label: L('视频监控', 'Video Monitor'), desc: L('接入站点安防摄像头，远程查看现场画面', 'Remote access to on-site security cameras') },
          { key: '/app/monitor/central', label: L('集控管理', 'Central Control'), desc: L('批量下发策略与电价，统一管理多站设备', 'Batch strategy/tariff dispatch for multiple stations') },
        ],
      },
      {
        key: 'sec-alarm', icon: AlertOutlined, label: L('报警中心', 'Alarm Center'),
        children: [
          { key: '/app/alarm/fault', label: L('故障报警', 'Fault Alarm'), desc: L('报警列表、报警统计与通知管理', 'Alarm list, statistics and notification management') },
          { key: '/app/alarm/battery', label: L('电池预警', 'Battery Warning'), desc: L('电池健康与异常预警，提前发现电池风险', 'Battery health and early warning') },
        ],
      },
      {
        key: 'sec-analysis', icon: LineChartOutlined, label: L('分析中心', 'Analysis Center'),
        children: [
          { key: '/app/analysis/report', label: L('统计报表', 'Statistics Report'), desc: L('电量与收益统计报表，支持导出', 'Energy and revenue reports with export') },
          { key: '/app/analysis/battery', label: L('电池分析', 'Battery Analysis'), desc: L('电压/电流/温度及落后电池报表分析', 'Voltage/current/temperature and lagging-cell reports') },
          { key: '/app/analysis/health', label: L('健康度分析', 'Health Analysis'), desc: L('设备与电池健康度评估', 'Health assessment for devices and batteries') },
        ],
      },
      {
        key: 'sec-om', icon: ToolOutlined, label: L('运维中心', 'O&M Center'),
        children: [
          { key: '/app/om/workorder', label: L('工单', 'Work Order'), desc: L('运维工单创建、派发与跟踪', 'Create, dispatch and track work orders') },
          { key: '/app/om/plan', label: L('计划', 'Plan'), desc: L('运维计划制定与执行', 'Create and execute O&M plans') },
          { key: '/app/om/graph', label: L('运维图谱', 'O&M Graph'), desc: L('设备与运维关系图谱', 'Device and O&M relationship graph') },
          { key: '/app/om/account', label: L('运维账号', 'O&M Account'), desc: L('运维人员账号管理', 'Manage O&M personnel accounts') },
        ],
      },
      {
        key: 'sec-biz', icon: FundOutlined, label: L('运营中心', 'Operation Center'),
        children: [
          { key: '/app/biz/response', label: L('响应管理', 'Demand Response'), desc: L('需求响应事件的申报与执行', 'Declare and execute demand response events') },
          { key: '/app/biz/settle', label: L('核算管理', 'Settlement'), desc: L('收益核算与结算（增值功能）', 'Revenue accounting and settlement (value-added)') },
          { key: '/app/biz/analysis', label: L('运营分析', 'Operation Analysis'), desc: L('运营指标分析与决策支持（增值功能）', 'Operation KPI analysis and decision support (value-added)') },
        ],
      },
    ],
  },
  {
    key: 'back',
    type: 'group',
    label: L('后台', 'Back Office'),
    children: [
      {
        key: 'sec-admin', icon: SettingOutlined, label: L('管理中心', 'Management Center'),
        children: [
          { key: '/app/admin/station', label: L('站点管理', 'Station Mgmt'), desc: L('站点档案、设备与拓扑配置', 'Station profile, devices and topology') },
          { key: '/app/admin/member', label: L('组织成员', 'Organization'), desc: L('组织架构、部门与成员数据权限', 'Org structure, departments and member data permissions') },
          { key: '/app/admin/role', label: L('角色管理', 'Role Mgmt'), desc: L('功能角色与权限配置', 'Functional roles and permission configuration') },
          { key: '/app/admin/company', label: L('外部公司', 'External Company'), desc: L('外部合作公司管理', 'Manage external partner companies') },
          { key: '/app/admin/log', label: L('日志管理', 'Log Mgmt'), desc: L('系统与操作日志查询', 'Query system and operation logs') },
          { key: '/app/admin/theme', label: L('个性配置', 'Appearance'), desc: L('平台名称与 Logo 个性化配置', 'Platform name and logo customization') },
        ],
      },
      {
        key: 'sec-customer', icon: ShopOutlined, label: L('客户中心', 'Customer Center'),
        children: [
          { key: '/app/customer/account', label: L('客户户号管理', 'Customer Account'), desc: L('客户户号与站点绑定管理', 'Bind customer accounts to stations') },
          { key: '/app/customer/asset', label: L('资产管理', 'Asset Mgmt'), desc: L('资产档案与生命周期管理', 'Asset profile and lifecycle management') },
        ],
      },
    ],
  },
];

// 展平所有叶子菜单
export const getLeaves = () => {
  const out = [];
  IA.forEach((g) => (g.children || []).forEach((s) => (s.children || []).forEach((leaf) => out.push({
    ...leaf,
    groupKey: g.key,
    groupLabel: g.label,
    sectionKey: s.key,
    sectionLabel: s.label,
  }))));
  return out;
};

// 按路径查找菜单元信息
export const findMenu = (path) => getLeaves().find((l) => l.key === path) || null;

// 生成 antd Menu items；hideKeys 用于按账号隐藏增值菜单（如非 super 隐藏三维/核算）
export const buildMenuItems = (lang, hideKeys = []) => {
  const hidden = new Set(hideKeys);
  return IA.map((g) => {
    const sections = (g.children || [])
      .map((s) => {
        const leaves = (s.children || [])
          .filter((leaf) => !hidden.has(leaf.key))
          .map((leaf) => ({ key: leaf.key, label: pick(leaf.label, lang) }));
        if (!leaves.length) return null;
        return {
          key: s.key,
          icon: React.createElement(s.icon),
          label: pick(s.label, lang),
          children: leaves,
        };
      })
      .filter(Boolean);
    if (!sections.length) return null;
    return {
      type: 'group',
      key: g.key,
      label: pick(g.label, lang),
      children: sections,
    };
  }).filter(Boolean);
};